import { inject, injectable } from "inversify";
import { TYPES } from "@utils/inversifyJS/types";
import type { IRawFileRepository } from "@domain/repositories/IRawFileRepository";
import { useUploadStore } from "@presentation/stores/UploadStore";
import axios from "axios";

const uuidv4 = () => {
  return "10000000-1000-4000-8000-100000000000".replaceAll(/[018]/g, (c: string) => (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16));
};

/**
A file whose name already exists in the target folder with a different size.
*/
export interface RawFileUploadConflict {
  file: File;
  existingSize: number;
}

export interface RawFileConflictCheck {
  /**
    Size of every already-stored object, keyed by file name. `null` means "does not exist".
    */
  existingSizes: Record<string, number | null>;
  conflicts: RawFileUploadConflict[];
}

/**
Returns the files that are safe to upload without replacing anything already stored.
*/
export const excludeConflicts = (files: File[], conflicts: RawFileUploadConflict[]): File[] => {
  const conflictingNames = new Set(conflicts.map((conflict) => conflict.file.name));
  return files.filter((file) => !conflictingNames.has(file.name));
};

@injectable()
export class UploadRawFileService {
  @inject(TYPES.IRawFileRepository)
  private _rawFileRepository!: IRawFileRepository;

  private _fileCache: Map<string, File> = new Map();

  private async checkExistingSizes(files: File[], folder: string): Promise<Record<string, number | null>> {
    try {
      const fileNames = files.map((f) => f.name);
      // Optimistically check in batch. If it fails, the result is empty and individual checks will run.
      return (await this._rawFileRepository.checkFilesExist(fileNames, folder)) || {};
    } catch (error) {
      console.warn("Batch file existence check failed, falling back to individual checks", error);
      return {};
    }
  }

  async uploadFile(file: File, folder: string, uploadId?: string, preCheckedSize?: number | null, uploadEventId?: string): Promise<void> {
    const id = uploadId || uuidv4();
    const cancelSource = axios.CancelToken.source();
    const { addUpload, updateProgress, setComplete, setFailed, setCancelled } = useUploadStore.getState();

    // If it's a new upload, add it to store and cache
    if (uploadId) {
      // Update cache in case we have a new file object (e.g. from re-select)
      this._fileCache.set(id, file);
      // If it's a resume, we might want to update the status to pending/uploading
      // and set the new cancel source
      useUploadStore.setState((state) => ({
        activeUploads: {
          ...state.activeUploads,
          [id]: {
            ...state.activeUploads[id],
            status: "pending",
            cancelSource,
            error: undefined,
          },
        },
      }));
    } else {
      addUpload(id, file.name, folder, cancelSource, "rawFile", file.size, uploadEventId);
      this._fileCache.set(id, file);
    }

    try {
      // 1. Check if file already exists on server
      let existingSize: number | null = null;

      existingSize = preCheckedSize === undefined ? await this._rawFileRepository.checkFileExists(file.name, folder) : preCheckedSize;

      if (existingSize !== null) {
        if (existingSize === file.size) {
          console.log(`File ${file.name} already exists with correct size. Skipping upload.`);
          updateProgress(id, file.size, file.size);
          setComplete(id);
          return;
        }
        if (existingSize < file.size) {
          console.log(`File ${file.name} exists but is incomplete (${existingSize}/${file.size}). Re-uploading.`);
          // Future: support range request to upload only the delta
        }
      }

      // 2. Start/Restart full upload
      updateProgress(id, 0, file.size);
      await this._rawFileRepository.uploadRawFile(
        file,
        folder,
        (percent) => {
          updateProgress(id, (percent / 100) * file.size, file.size);
        },
        cancelSource.token,
      );

      setComplete(id);
    } catch (error: unknown) {
      if (axios.isCancel(error)) {
        setCancelled(id);
      } else {
        setFailed(id, error instanceof Error ? error.message : "Upload failed");
      }
      throw error;
    }
  }

  async resumeUpload(fileOrId: File | string, folder?: string, uploadId?: string): Promise<void> {
    if (typeof fileOrId === "string") {
      const cachedFile = this._fileCache.get(fileOrId);
      if (!cachedFile) {
        throw new Error("File not found in cache. Please re-select the file.");
      }
      // If folder is not provided, we should ideally get it from store or keep it in cache
      // But resumeUpload is usually called with all info from the UI
      return this.uploadFile(cachedFile, folder!, fileOrId);
    }
    return this.uploadFile(fileOrId, folder!, uploadId);
  }

  hasFileInCache(uploadId: string): boolean {
    return this._fileCache.has(uploadId);
  }

  /**
   * Looks up which of the given files already exist in the target folder and reports the ones
   * that would be destructively overwritten.
   *
   * A file is only reported as a conflict when an object with the same name exists but has a
   * *different* size. A same-name/same-size match is treated as an already-completed upload and
   * is skipped silently by `uploadFile`, so surfacing it would spam the user on every retry.
   *
   * The check is best-effort: if the backend lookup fails we report no conflicts so that the
   * upload can still proceed, matching the pre-existing fail-open behaviour.
   */
  async findUploadConflicts(files: File[], folder: string): Promise<RawFileConflictCheck> {
    const existingSizes = await this.checkExistingSizes(files, folder);

    const conflicts = files
      .map((file) => ({ file, existingSize: existingSizes[file.name] }))
      .filter((candidate): candidate is RawFileUploadConflict => candidate.existingSize !== undefined && candidate.existingSize !== null && candidate.existingSize !== candidate.file.size);

    return { existingSizes, conflicts };
  }

  async uploadMultipleFiles(files: File[], folder: string, preCheckedSizes?: Record<string, number | null>): Promise<void> {
    if (files.length === 0) return;

    const uploadEventId = uuidv4();
    const uploadIdsByFile = new Map<File, string>();
    const { addUpload } = useUploadStore.getState();

    for (const file of files) {
      const uploadId = uuidv4();
      uploadIdsByFile.set(file, uploadId);
      addUpload(uploadId, file.name, folder, undefined, "rawFile", file.size, uploadEventId);
      this._fileCache.set(uploadId, file);
    }

    // 1. Batch check existence, unless the caller already did it (e.g. to prompt about conflicts).
    const existingSizes = preCheckedSizes ?? (await this.checkExistingSizes(files, folder));

    // 2. Concurrency limiting
    const CONCURRENCY_LIMIT = 6;
    const queue = [...files];

    const worker = async () => {
      while (queue.length > 0) {
        const file = queue.shift();
        if (!file) break;

        const preCheckedSize = existingSizes[file.name] === undefined ? undefined : existingSizes[file.name];
        const uploadId = uploadIdsByFile.get(file);
        if (!uploadId) continue;

        try {
          await this.uploadFile(file, folder, uploadId, preCheckedSize, uploadEventId);
        } catch {
          // Error is already handled in uploadFile (setting status to failed)
          // We just continue to next file
        }
      }
    };

    const workers = Array.from({ length: Math.min(files.length, CONCURRENCY_LIMIT) }, () => worker());
    await Promise.all(workers);
  }
}
